import React from "react";
import ServiceItem from "./ServiceItem";

const servicesData = [
  {
    serviceNumber: "SERVICE 01",
    title: "Translation",
    reversed: false,
    theme: "light",
    paragraphs: [
      "Our professional translation services help businesses communicate accurately across languages while preserving the original meaning, tone, context, and intent of the content.",
      "Our linguists carefully translate documents, business content, technical material, websites, applications, marketing content, and other language assets according to the specific requirements of each project. The translation process focuses not only on word-to-word conversion but also on cultural relevance, terminology consistency, sentence structure, and readability.",
      "Each translation is handled by qualified language professionals who understand the nuances of the target language and ensure that the final content reads naturally to its intended audience.",
    ],
    steps: [
      "Understand the source content and project requirements.",
      "Assign the work to appropriate language professionals.",
      "Translate while maintaining meaning, tone, terminology, and context.",
      "Apply terminology and formatting requirements.",
      "Perform quality checks before final delivery.",
    ],
  },
  {
    serviceNumber: "SERVICE 02",
    title: "Transcription",
    reversed: true,
    theme: "tinted",
    paragraphs: [
      "Our transcription services convert audio and video recordings into accurate, readable text. We work with a variety of content, including interviews, meetings, conversations, training materials, recordings, and other audio or video files.",
      "Our team listens carefully to the source recording and converts spoken content into written form while maintaining the structure and context of the original material. Depending on project requirements, we can also follow specific formatting, speaker identification, timestamps, and other transcription guidelines.",
    ],
    steps: [
      "Review the audio or video and understand the project requirements.",
      "Transcribe the spoken content accurately.",
      "Identify speakers and apply timestamps where required.",
      "Review the transcript against the original recording.",
      "Correct errors and deliver a clean, structured transcript.",
    ],
  },
  {
    serviceNumber: "SERVICE 03",
    title: "Voice Recording",
    reversed: false,
    theme: "light",
    paragraphs: [
      "We provide professional voice-recording services for projects that require high-quality human voice data or recorded content in specific languages, accents, and speaking styles.",
      "Our in-office recording process is designed around project-specific scripts, recording guidelines, speaker requirements, and quality standards. Voice contributors are guided to maintain appropriate pronunciation, tone, pace, clarity, and consistency throughout the recording process.",
      "Recorded files are reviewed for audio quality, pronunciation, completeness, background noise, and adherence to the provided requirements before being prepared for delivery.",
    ],
    steps: [
      "Review scripts and recording specifications.",
      "Select appropriate voice contributors based on language and project requirements.",
      "Conduct recordings in a controlled office environment.",
      "Monitor pronounciation, pacing, clarity and consistency.",
      "Perform quality checks on recorded files.",
      "Organise and prepare the final audio data according to client specifications.",
    ],
  },
  {
    serviceNumber: "SERVICE 04",
    title: "Proofreading",
    reversed: true,
    theme: "dark",
    paragraphs: [
      "Our proofreading service focuses on identifying and correcting language errors in already translated or written content. This helps ensure that the final material is accurate, polished, professional, and ready for publication or use.",
      "Our proofreaders carefully review content for spelling, grammar, punctuation, sentence structure, terminology, formatting, and consistency while ensuring that the original meaning remains unchanged.",
    ],
    steps: [
      "Review the completed content against the applicable language and project guidelines.",
      "Identify spelling, grammar, punctuation, and formatting issues.",
      "Check terminology and consistency throughout the content.",
      "Correct identified errors without changing the intended meaning.",
      "Conduct a final quality review before delivery.",
    ],
  },
  {
    serviceNumber: "SERVICE 05",
    title: "Editing",
    reversed: false,
    theme: "light",
    paragraphs: [
      "Our editing services improve the overall quality, readability, and effectiveness of written content. While proofreading primarily focuses on correcting errors, editing goes a step further by improving sentence structure, language flow, clarity, and consistency.",
      "Our editors review the content from a reader’s perspective and make appropriate language improvements while maintaining the original message, tone, and intent.",
    ],
    steps: [
      "Review the content for clarity, structure, and readability.",
      "Identify awkward, unclear, or inconsistent language.",
      "Improve sentence construction and overall flow.",
      "Maintain appropriate terminology, tone, and style.",
      "Review the edited content to ensure that the intended meaning is preserved.",
    ],
  },
  {
    serviceNumber: "SERVICE 06",
    title: "Reviewing",
    reversed: true,
    theme: "tinted",
    paragraphs: [
      "Our language-review service provides an additional layer of quality assurance by evaluating completed translations or language content against the source material and project requirements.",
      "Reviewers assess whether the content accurately communicates the source meaning and whether it meets the required linguistic, contextual, and quality standards. This process helps identify issues that may not be immediately visible during the initial translation or editing stage.",
    ],
    steps: [
      "Compare the target content with the source material.",
      "Check accuracy, completeness, terminology, grammar, and context.",
      "Identify mistranslations, omissions, inconsistencies, and language issues.",
      "Validate adherence to client-specific guidelines.",
      "Provide corrections and recommendations where required.",
      "Complete a final quality assessment before delivery.",
    ],
  },
  {
    serviceNumber: "SERVICE 07",
    title: "MTPE - Machine Translation Proofreading And Editing",
    reversed: false,
    theme: "light",
    paragraphs: [
      "Our Machine Translation Proofreading and Editing (MTPE) service combines the efficiency of machine translation with the expertise of professional linguists.",
      "Machine-translated content is reviewed and refined by our language professionals to improve accuracy, fluency, terminology, grammar, and overall readability. Depending on the project requirements, we perform either light post-editing for basic comprehensibility or full post-editing for content that requires a higher level of linguistic quality.",
      "Our linguists ensure that machine-generated translations accurately convey the original meaning and meet the required language and quality standards.",
    ],
    steps: [
      "Process the source content through the specified machine translation system.",
      "Review the machine-generated translation against the source.",
      "Identify mistranslations, omissions, grammatical errors, and unnatural language.",
      "Correct terminology and language inconsistencies.",
      "Improve fluency, readability, and contextual accuracy.",
      "Perform a final quality check against project requirements.",
    ],
  },
];

const LanguageExpertiseServices = () => {
  return (
    <section className="w-full bg-white pt-16 sm:pt-20 lg:pt-24">
      {/* Header section with title and description */}
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 items-end pb-8 sm:pb-12 border-b border-gray-200"
          data-aos="fade-up"
        >
          <div className="lg:col-span-7">
            <span className="text-[#EF8702] text-sm sm:text-base font-black tracking-[0.18em] uppercase mb-3 block font-['Axiforma',sans-serif]">
              OUR LANGUAGE SERVICES
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-[44px] font-extrabold text-[#111827] tracking-tight leading-[1.3] lg:leading-[1.32] font-['Axiforma',sans-serif]">
              Languages Expertise <br className="hidden sm:inline" />
              Beyond Translation
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[#4B5563] text-sm sm:text-base leading-relaxed font-normal">
              Our experienced linguists and language professionals follow defined processes and quality checks to deliver reliable, client-ready content.
            </p>
          </div>
        </div>
      </div>

      {/* Services 01 - 07 */}
      <div className="flex flex-col w-full">
        {servicesData.map((service, index) => (
          <ServiceItem key={index} {...service} />
        ))}
      </div>
    </section>
  );
};

export default LanguageExpertiseServices;
